import { hapticTap, hapticSuccess, hapticError } from '@/lib/haptics';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useBusinessOwnerGuard } from '@/hooks/useBusinessOwnerGuard';
import { invalidateFriends } from '@/lib/socialBatch';

interface FriendRequest {
  id: string;
  sender_id: string;
  receiver_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  note?: string | null;
}

interface UseFriendRequestsOptions {
  onAuthRequired?: () => void;
}

export const useFriendRequests = (targetUserId?: string, options?: UseFriendRequestsOptions) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { guard, dialog: businessGuardDialog } = useBusinessOwnerGuard();
  const [friendRequest, setFriendRequest] = useState<FriendRequest | null>(null);
  const [isFriend, setIsFriend] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchFriendStatus = async () => {
    if (!user || !targetUserId) {
      setLoading(false);
      return;
    }

    try {
      // Check if there's a friend request between these users
      const { data, error } = await supabase
        .from('friend_requests')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${targetUserId}),and(sender_id.eq.${targetUserId},receiver_id.eq.${user.id})`)
        .maybeSingle();

      if (error) throw error;

      setFriendRequest(data as FriendRequest | null);
      setIsFriend(data?.status === 'accepted');
    } catch (error) {
      console.error('Error fetching friend status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFriendStatus();
  }, [user, targetUserId]);

  const sendFriendRequest = async (receiverId: string, note?: string) => {
    if (!user) {
      if (options?.onAuthRequired) {
        options.onAuthRequired();
      }
      return { authRequired: true };
    }
    if (guard("to say hi")) return { businessBlocked: true };

    try {
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: user.id,
          receiver_id: receiverId,
          ...(note ? { note } : {}),
        });

      if (error) throw error;

      // Send push notification to receiver
      try {
        await supabase.functions.invoke('send-friend-request-notification', {
          body: { receiverId, type: 'friend_request' }
        });
      } catch (pushError) {
        console.log('Push notification not sent (user may not have notifications enabled)');
      }

      // Send email notification to receiver
      try {
        await supabase.functions.invoke('send-friend-request-email', {
          body: { receiverId, type: 'friend_request', note }
        });
      } catch (emailError) {
        console.log('Email notification not sent:', emailError);
      }

      await fetchFriendStatus();

      hapticSuccess();
      toast({
        title: "Hi sent 🌸",
        description: "They'll get a friendly notification.",
      });
      return { authRequired: false, success: true };
    } catch (error: any) {
      console.error('Error sending friend request:', error);
      hapticError();
      toast({
        title: "Error",
        description: error.message || "Couldn't send your hi",
        variant: "destructive",
      });
      return { authRequired: false, success: false };
    }
  };

  const acceptFriendRequest = async (requestId: string) => {
    try {
      // First, get the friend request to know who to notify
      const { data: request, error: fetchError } = await supabase
        .from('friend_requests')
        .select('sender_id')
        .eq('id', requestId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'accepted' })
        .eq('id', requestId);

      if (error) throw error;

      // The shared friends cache is now stale
      invalidateFriends();

      // Seed the original invitation note as the first direct message
      // so both users see the context (e.g. "We met at Bertie and Boo").
      try {
        await supabase.rpc('seed_friend_request_note_message', {
          _request_id: requestId,
        });
      } catch (seedError) {
        console.log('Could not seed friend request note message:', seedError);
      }

      // Send push notification to the sender
      if (request?.sender_id) {
        try {
          await supabase.functions.invoke('send-friend-request-notification', {
            body: { receiverId: request.sender_id, type: 'friend_accept' }
          });
        } catch (pushError) {
          console.log('Push notification not sent (user may not have notifications enabled)');
        }

        // Send email notification to the sender
        try {
          await supabase.functions.invoke('send-friend-request-email', {
            body: { receiverId: request.sender_id, type: 'friend_accept' }
          });
        } catch (emailError) {
          console.log('Email notification not sent:', emailError);
        }
      }


      await fetchFriendStatus();

      toast({
        title: "You're now friends 🎉",
        description: "Say hello and start chatting.",
      });
    } catch (error) {
      console.error('Error accepting friend request:', error);
      toast({
        title: "Error",
        description: "Couldn't accept",
        variant: "destructive",
      });
    }
  };

  const rejectFriendRequest = async (requestId: string) => {
    try {
      // Delete the request instead of marking as rejected
      // This allows either party to send a new friend request
      const { error } = await supabase
        .from('friend_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      invalidateFriends();
      setFriendRequest(null);
      setIsFriend(false);

      toast({
        title: "Success",
        description: "Friend request declined",
      });
    } catch (error) {
      console.error('Error declining friend request:', error);
      toast({
        title: "Error",
        description: "Failed to decline friend request",
        variant: "destructive",
      });
    }
  };

  const removeFriend = async (friendUserId: string) => {
    if (!user) return;

    try {
      // Delete the friend request record (works for both sender and receiver)
      const { error } = await supabase
        .from('friend_requests')
        .delete()
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${friendUserId}),and(sender_id.eq.${friendUserId},receiver_id.eq.${user.id})`);

      if (error) throw error;

      invalidateFriends();
      setFriendRequest(null);
      setIsFriend(false);

      toast({
        title: "Success",
        description: "Friend removed",
      });
    } catch (error) {
      console.error('Error removing friend:', error);
      toast({
        title: "Error",
        description: "Failed to remove friend",
        variant: "destructive",
      });
    }
  };

  return {
    friendRequest,
    isFriend,
    loading,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    refetch: fetchFriendStatus,
    businessGuardDialog,
    businessGuard: guard,
  };
};
